// Import package components
import classnames from "classnames";
import { useEffect, useRef, useState } from "react";
import { normalizeSmsPaste } from "../../../utils/smsMessageLength";

// Import common components
import MacrosReservationKeys from "../MacroReservationKeys";
import Responses from "../Responses";
import ChatbotService from "../../../services/chatbot.service";

const SMSEmailInputbox = ({
  label = "Text*",
  onChange,
  value,
  required,
  readOnly,
  error,
  macrosOrResponse,
  showSavedResponses = true,
  onSavedReply,
  limit = true,
  optimizeWithAI = false,
  optimizationSource,
  optimizationTrigger = "button",
  normalizeOnPaste = false,
  messageLengthStatus,
  allowIdentityVerification = false,
}) => {
  const textareaRef = useRef(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimization, setOptimization] = useState(null);
  const [optimizationError, setOptimizationError] = useState("");

  useEffect(() => {
    if (
      optimization &&
      value !== optimization.requested_message &&
      value !== optimization.suggested_message
    ) {
      setOptimization(null);
    }
  }, [optimization, value]);

  // Insert macro at cursor position
  const handleInsertMacro = (macro) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = value.slice(0, start) + macro + value.slice(end);
    // Move cursor after inserted macro
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + macro.length;
    }, 0);
    // Call onChange with synthetic event
    onChange({ target: { value: newValue } });
  };

  const handleOptimize = async () => {
    if (!value?.trim() || isOptimizing) return;

    const requestedMessage = value;
    setIsOptimizing(true);
    setOptimizationError("");
    setOptimization(null);

    try {
      const response = await ChatbotService.optimizeMessage(
        requestedMessage,
        optimizationSource,
      );
      setOptimization({
        ...response.data,
        requested_message: requestedMessage,
      });
    } catch (error) {
      console.error("Error optimizing SMS message:", error);
      setOptimizationError(
        "We couldn't optimize this message. Please try again.",
      );
    } finally {
      setIsOptimizing(false);
    }
  };

  const applyOptimization = () => {
    onChange({ target: { value: optimization.suggested_message } });
    setOptimization(null);
  };

  const handlePaste = (event) => {
    if (!normalizeOnPaste) return;

    const pastedText = event.clipboardData.getData("text");
    const normalizedText = normalizeSmsPaste(pastedText);
    if (normalizedText === pastedText) return;

    event.preventDefault();
    const textarea = event.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const availableCharacters = limit
      ? Math.max(0, 612 - (value.length - (end - start)))
      : normalizedText.length;
    const insertedText = normalizedText.slice(0, availableCharacters);
    const newValue =
      value.slice(0, start) + insertedText + value.slice(end);

    onChange({ target: { value: newValue } });
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd =
        start + insertedText.length;
    }, 0);
  };

  return (
    <>
      <div className="uk-form-controls">
        <div className="uk-width-1-1 uk-grid-margin uk-first-column">
          <label className="uk-form-label">Message*</label>
        </div>
        <textarea
          ref={textareaRef}
          className={classnames("uk-textarea", { error })}
          rows="8"
          maxLength={limit ? 612 : -1}
          onChange={onChange}
          onPaste={handlePaste}
          value={value}
          required={required}
          readOnly={readOnly}
        />
      </div>
      <div className="large-msg-typing-btn">
        <div className="uk-flex uk-flex-between uk-flex-middle">
          <div className="uk-flex uk-flex-middle sms-composer-actions">
            {macrosOrResponse ? (
              <div>
                <MacrosReservationKeys
                  onMacroClick={handleInsertMacro}
                  allowIdentityVerification={allowIdentityVerification}
                />
              </div>
            ) : showSavedResponses ? (
              <div className="msg-editing-btn">
                <Responses onChange={onSavedReply} />
              </div>
            ) : null}
            {optimizeWithAI && limit && !readOnly && (
              <button
                type="button"
                className={classnames(
                  "uk-button uk-button-small sms-ai-optimize-button",
                  {
                    "uk-button-default": optimizationTrigger !== "icon",
                    "sms-ai-optimize-icon": optimizationTrigger === "icon",
                  },
                )}
                onClick={handleOptimize}
                disabled={isOptimizing || !value?.trim()}
                aria-label="Optimize with Rue AI"
                title="Optimize with Rue AI"
                uk-tooltip="Optimize with Rue AI"
              >
                {isOptimizing ? (
                  <span data-uk-spinner="ratio: 0.45"></span>
                ) : optimizationTrigger === "icon" ? (
                  <img src="/images/ai-guest-reply.svg" alt="" />
                ) : (
                  "Optimize with Rue AI"
                )}
              </button>
            )}
            {/*
            <a href="/ai-assistant"
              uk-tooltip="AI Assistant"
              target="_blank"
              rel="noopener noreferrer" 
            >
              <Image src="/images/ai-assistant.svg" />
            </a>
             */}
          </div>

          {limit && (
            <div className="scheduler-message-length-footer">
              {messageLengthStatus}
              <div className="hms-small-note">
                Characters Remaining: {612 - value?.length}
              </div>
            </div>
          )}
        </div>
      </div>
      {optimization && (
        <div className="uk-alert-primary uk-margin-small-top">
          <strong>Rue AI suggestion</strong>
          <div className="uk-margin-small-top uk-text-break">
            {optimization.suggested_message}
          </div>
          <div className="uk-margin-small-top">
            <button
              type="button"
              className="uk-button hms-btn hms-red-btn uk-button-small"
              onClick={applyOptimization}
            >
              Use suggestion
            </button>
            <button
              type="button"
              className="uk-button hms-btn hms-white-btn"
              onClick={() => setOptimization(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {optimizationError && (
        <div className="uk-alert-danger uk-margin-small-top">
          {optimizationError}
        </div>
      )}
    </>
      );
};

export default SMSEmailInputbox;
